/**
 * Kshetra Veda - Universal GSAP Magnetic Attraction & Interactive Animation Suite
 * Features:
 * 1. 3D Mouse Tilt on Product & Lab Cards
 * 2. Universal GSAP Magnetic Attraction on Profile/Account Icons, Header Icons, Nav Links, Category Pills, Cards & Buttons
 * 3. Tactile Click Ripples
 * 4. GSAP ScrollTrigger Entrance Reveals
 * 5. Framer-Style Letter-by-Letter Reveal for Homepage Hero Text
 */
(() => {
  if (window.KshetraAnimationsInitialized) return;
  window.KshetraAnimationsInitialized = true;

  // Load GSAP + ScrollTrigger CDN automatically if not present
  function loadGSAP(callback) {
    if (window.gsap && window.ScrollTrigger) {
      if (callback) callback();
      return;
    }

    const script1 = document.createElement('script');
    script1.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
    script1.onload = () => {
      const script2 = document.createElement('script');
      script2.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';
      script2.onload = () => {
        window.gsap.registerPlugin(window.ScrollTrigger);
        if (callback) callback();
      };
      document.head.appendChild(script2);
    };
    document.head.appendChild(script1);
  }

  // 1. 3D Mouse Tilt on Cards (excluding product cards)
  function init3DTiltCards() {
    const cards = document.querySelectorAll('.kv-lab-card');
    cards.forEach((card) => {
      card.style.transition = 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s ease';
      card.style.transformStyle = 'preserve-3d';

      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const rotateX = ((y - centerY) / centerY) * -6;
        const rotateY = ((x - centerX) / centerX) * 6;

        card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(6px)`;
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0px)';
      });
    });
  }

  // 2. Universal GSAP Magnetic Attraction
  function initUniversalMagnetic() {
    if (!window.gsap) return;

    const magneticSelector = [
      '.header__icon',
      '.header__icon--search',
      '.header__icon--account',
      '.header__icon--cart',
      '.account-button__icon-wrapper',
      '.account-button__icon',
      '.header-actions__action',
      '.header__menu-item',
      '.header__inline-menu a',
      '.menu-drawer__menu-item',
      '.kv-shop-tabs button',
      '.kv-lab-tab-btn',
      '.btn',
      '.button',
      'a.button',
      'button:not(.slick-arrow)',
      '.kv-lab-download-btn',
      '[data-magnetic]'
    ].join(', ');

    const elements = document.querySelectorAll(magneticSelector);

    elements.forEach((el) => {
      if (el.dataset.magneticAttached) return;
      el.dataset.magneticAttached = 'true';

      el.style.willChange = 'transform';

      // Determine magnetic strength based on element type
      let strength = 0.26;
      if (
        el.matches(
          '.header__icon, .header__icon--account, .account-button__icon-wrapper, .account-button__icon, .header-actions__action, .header__inline-menu a, .menu-drawer__menu-item'
        )
      ) {
        strength = 0.35;
      }

      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - (rect.left + rect.width / 2);
        const y = e.clientY - (rect.top + rect.height / 2);

        window.gsap.to(el, {
          x: x * strength,
          y: y * strength,
          duration: 0.3,
          ease: 'power2.out'
        });
      });

      el.addEventListener('mouseleave', () => {
        window.gsap.to(el, {
          x: 0,
          y: 0,
          duration: 0.65,
          ease: 'elastic.out(1.1, 0.4)'
        });
      });
    });
  }

  // 3. Tactile Click Ripple Feedback
  function initClickRipple() {
    const targets = document.querySelectorAll(
      '.button, .btn, .kv-shop-tabs button, .kv-lab-tab-btn, .kv-lab-download-btn, .header__icon'
    );
    targets.forEach((el) => {
      if (el.dataset.rippleAttached) return;
      el.dataset.rippleAttached = 'true';

      el.addEventListener('click', (e) => {
        const rect = el.getBoundingClientRect();
        const circle = document.createElement('span');
        const diameter = Math.max(rect.width, rect.height);
        const radius = diameter / 2;

        circle.style.width = circle.style.height = `${diameter}px`;
        circle.style.left = `${e.clientX - rect.left - radius}px`;
        circle.style.top = `${e.clientY - rect.top - radius}px`;
        circle.classList.add('kv-click-ripple');

        const existingRipple = el.querySelector('.kv-click-ripple');
        if (existingRipple) existingRipple.remove();

        el.appendChild(circle);
        setTimeout(() => circle.remove(), 600);
      });
    });
  }

  // 4. GSAP ScrollTrigger Entrance Animations
  function initScrollTriggers() {
    if (!window.gsap || !window.ScrollTrigger) return;

    const targets = document.querySelectorAll('.kv-shop-card, .section-header, .kv-lab-card, .marquee--brand-strip');
    targets.forEach((el) => {
      window.gsap.fromTo(
        el,
        { opacity: 0.82, y: 18 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            toggleActions: 'play none none none'
          }
        }
      );
    });
  }

  // 5. Framer-Style Letter-by-Letter Reveal for Homepage Hero Text Only
  function initHeroLetterReveal() {
    if (!window.gsap) return;

    const heroHeadline = document.querySelector('.kv-hero-carousel__headline h1');
    const heroAccent = document.querySelector('.kv-hero-carousel__headline h2');
    const heroText = document.querySelector('.kv-hero-carousel__text');

    const targets = [heroHeadline, heroAccent, heroText].filter(Boolean);

    targets.forEach((el) => {
      if (el.dataset.letterRevealed) return;
      el.dataset.letterRevealed = 'true';

      const rawText = el.textContent.trim();
      if (!rawText || rawText.length < 3) return;

      const chars = rawText.split('');
      el.innerHTML = chars
        .map((ch) => {
          if (ch === ' ') return '<span style="display:inline-block; width:0.25em;"> </span>';
          return `<span class="kv-reveal-char" style="display:inline-block; opacity:0.12; transform:translateY(6px); will-change:opacity,transform;">${ch}</span>`;
        })
        .join('');

      const spanChars = el.querySelectorAll('.kv-reveal-char');

      window.gsap.to(spanChars, {
        opacity: 1,
        y: 0,
        stagger: 0.02,
        duration: 0.35,
        ease: 'power2.out',
        delay: 0.2
      });
    });
  }

  // Inject helper ripple style
  const style = document.createElement('style');
  style.textContent = `
    .button, .btn, .kv-shop-tabs button, .kv-lab-tab-btn, .kv-lab-download-btn, .header__icon {
      position: relative;
      overflow: hidden;
    }
    span.kv-click-ripple {
      position: absolute;
      border-radius: 50%;
      transform: scale(0);
      animation: kv-ripple-animation 600ms linear;
      background-color: rgba(255, 255, 255, 0.4);
      pointer-events: none;
    }
    @keyframes kv-ripple-animation {
      to {
        transform: scale(2.5);
        opacity: 0;
      }
    }
  `;
  document.head.appendChild(style);

  // 6. Header Color Controller - White header background active, keep standard green brand text & icons
  function initHeaderScrollColor() {
    document.body.classList.remove('kv-header-at-top-home', 'kv-header-at-top');
    const targets = document.querySelectorAll('#header-component, .header-section, .header, header');
    targets.forEach((el) => el.classList.remove('kv-header-scroll-top-home'));
  }

  // 7. Universal Section & Image Scrub Parallax for Our Story, Journal, Recipes, & Lab Reports
  function initSectionParallax() {
    if (!window.gsap || !window.ScrollTrigger) return;

    // 1. Image & Media Parallax Shift on Scroll
    const images = document.querySelectorAll(
      '.kv-story-image, .kv-story-image-panel img, .kv-story-image-panel video, .journal-featured-card__image, .journal-story-card__image, .kv-recipes-featured-image img, .kv-recipes-card-image img'
    );

    images.forEach((img) => {
      if (img.dataset.parallaxActive) return;
      img.dataset.parallaxActive = 'true';

      const parentContainer =
        img.closest(
          '.kv-story-image-panel, .journal-featured-card__image-wrap, .journal-story-card__image-wrap, .kv-recipes-featured-image, .kv-recipes-card-image'
        ) || img.parentElement;

      if (parentContainer) {
        parentContainer.style.overflow = 'hidden';
      }

      window.gsap.fromTo(
        img,
        { scale: 1.14, yPercent: -8 },
        {
          scale: 1.02,
          yPercent: 8,
          ease: 'none',
          scrollTrigger: {
            trigger: parentContainer || img,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.6
          }
        }
      );
    });

    // 2. Multi-Section Fade & Smooth Depth Parallax
    const storySections = document.querySelectorAll(
      '.kv-story-split, .kv-story-values, .kv-story-closing, .journal-featured, .journal-more, .kv-recipes-featured, .kv-recipes-all'
    );

    storySections.forEach((sec) => {
      if (sec.dataset.secParallaxActive) return;
      sec.dataset.secParallaxActive = 'true';

      const textInner = sec.querySelector(
        '.kv-story-copy-panel, .kv-story-mission-panel, .kv-story-shell, .journal-featured-card__content, .journal-more__grid'
      );

      if (textInner) {
        window.gsap.fromTo(
          textInner,
          { y: 36, opacity: 0.88 },
          {
            y: 0,
            opacity: 1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: sec,
              start: 'top 85%',
              end: 'top 35%',
              scrub: 0.5
            }
          }
        );
      }
    });

    // 3. Shop Hero Background & Copy Scrub Parallax
    const shopHero = document.querySelector('.kv-shop-hero');
    if (shopHero && !shopHero.dataset.parallaxActive) {
      shopHero.dataset.parallaxActive = 'true';
      const heroCopy = shopHero.querySelector('.kv-shop-hero-copy');
      if (heroCopy) {
        window.gsap.to(heroCopy, {
          yPercent: 18,
          opacity: 0.85,
          ease: 'none',
          scrollTrigger: {
            trigger: shopHero,
            start: 'top top',
            end: 'bottom top',
            scrub: 0.5
          }
        });
      }
    }
  }

  // Run initializers
  document.addEventListener('DOMContentLoaded', () => {
    init3DTiltCards();
    initClickRipple();
    initHeaderScrollColor();

    loadGSAP(() => {
      initScrollTriggers();
      initUniversalMagnetic();
      initSectionParallax();
      setTimeout(initHeroLetterReveal, 150);
    });
  });

  // Also trigger immediately if DOM is ready
  if (document.readyState !== 'loading') {
    init3DTiltCards();
    initClickRipple();
    initHeaderScrollColor();
    loadGSAP(() => {
      initScrollTriggers();
      initUniversalMagnetic();
      initSectionParallax();
      setTimeout(initHeroLetterReveal, 150);
    });
  }
})();
